import { AuthService } from '../services/AuthService';
import { AuthRepositoryFactory } from '../repositories/AuthRepositoryFactory';
import { SessionId } from '../value-objects/SessionId';
import { UserId } from '../value-objects/UserId';
import { AuthLocalStorageAdapter } from '../repositories/AuthLocalStorageAdapter';

/**
 * 로그아웃 요청 DTO
 */
export interface LogoutRequest {
  sessionId?: string;
  logoutFromAllDevices?: boolean;
  reason?: 'user_initiated' | 'security' | 'admin' | 'system' | 'timeout';
  metadata?: {
    userAgent?: string;
    ipAddress?: string;
    deviceName?: string;
  };
}

/**
 * 로그아웃 응답 DTO
 */
export interface LogoutResponse {
  success: boolean;
  message?: string;
  error?: string;
  errorCode?: string;
  metadata?: {
    sessionsCleared?: number;
    securityEventRecorded?: boolean;
    localDataCleared?: boolean;
  };
}

/**
 * 로그아웃 Use Case
 * 사용자 로그아웃 프로세스를 담당하는 애플리케이션 서비스
 */
export class LogoutUseCase {
  private authService: AuthService;

  constructor() {
    const repository = AuthRepositoryFactory.getInstance();
    this.authService = new AuthService(repository);
  }

  /**
   * 로그아웃 실행
   */
  async execute(request: LogoutRequest = {}): Promise<LogoutResponse> {
    try {
      // 1. 현재 사용자 세션 정보 확인
      const currentSession = AuthLocalStorageAdapter.loadSession();
      const currentUser = AuthLocalStorageAdapter.loadUser();

      if (!currentSession && !currentUser) {
        return {
          success: true,
          message: '이미 로그아웃된 상태입니다.',
          metadata: {
            sessionsCleared: 0,
            securityEventRecorded: false,
            localDataCleared: true,
          },
        };
      }

      // 2. 로그아웃 유형에 따른 처리
      if (request.logoutFromAllDevices && currentUser) {
        return await this.logoutFromAllDevices(currentUser.id, request);
      } else {
        return await this.logoutFromCurrentSession(request.sessionId || currentSession?.id, request);
      }

    } catch (error) {
      console.error('로그아웃 Use Case 실행 중 오류:', error);
      
      // 오류가 발생해도 로컬 데이터는 정리
      AuthLocalStorageAdapter.clearAll();
      
      return {
        success: false,
        error: '로그아웃 처리 중 오류가 발생했습니다.',
        errorCode: 'LOGOUT_ERROR',
        metadata: {
          sessionsCleared: 0,
          securityEventRecorded: false,
          localDataCleared: true,
        },
      };
    }
  }

  /**
   * 현재 세션에서만 로그아웃
   */
  private async logoutFromCurrentSession(
    sessionId?: string,
    request: LogoutRequest = {}
  ): Promise<LogoutResponse> {
    try {
      const targetSessionId = sessionId ? SessionId.fromString(sessionId) : undefined;
      
      // AuthService를 통한 로그아웃 처리
      await this.authService.logout(targetSessionId);

      // 로그아웃 사유 기록
      await this.recordLogoutEvent(request.reason || 'user_initiated', request.metadata);

      return {
        success: true,
        message: '성공적으로 로그아웃되었습니다.',
        metadata: {
          sessionsCleared: 1,
          securityEventRecorded: true,
          localDataCleared: true,
        },
      };

    } catch (error) {
      console.error('단일 세션 로그아웃 중 오류:', error);
      
      // 로컬 데이터는 정리
      AuthLocalStorageAdapter.clearAll();
      
      return {
        success: false,
        error: '로그아웃 처리 중 일부 오류가 발생했지만, 로컬에서는 로그아웃되었습니다.',
        errorCode: 'PARTIAL_LOGOUT_ERROR',
        metadata: {
          sessionsCleared: 0,
          securityEventRecorded: false,
          localDataCleared: true,
        },
      };
    }
  }

  /**
   * 모든 기기에서 로그아웃
   */
  private async logoutFromAllDevices(
    userId: string,
    request: LogoutRequest = {}
  ): Promise<LogoutResponse> {
    try {
      const userIdObj = UserId.fromString(userId);
      
      // AuthService를 통한 전체 로그아웃 처리
      await this.authService.logoutFromAllDevices(userIdObj);

      // 보안 이벤트 기록
      await this.recordLogoutEvent('user_initiated_all_devices', request.metadata, userIdObj);

      return {
        success: true,
        message: '모든 기기에서 성공적으로 로그아웃되었습니다.',
        metadata: {
          sessionsCleared: -1, // -1은 모든 세션을 의미
          securityEventRecorded: true,
          localDataCleared: true,
        },
      };

    } catch (error) {
      console.error('전체 기기 로그아웃 중 오류:', error);
      
      // 로컬 데이터는 정리
      AuthLocalStorageAdapter.clearAll();
      
      return {
        success: false,
        error: '일부 기기에서 로그아웃 처리 중 오류가 발생했을 수 있습니다. 현재 기기에서는 로그아웃되었습니다.',
        errorCode: 'PARTIAL_MULTI_LOGOUT_ERROR',
        metadata: {
          sessionsCleared: 0,
          securityEventRecorded: false,
          localDataCleared: true,
        },
      };
    }
  }

  /**
   * 자동 로그아웃 (시스템 타임아웃 등)
   */
  async executeAutoLogout(reason: 'timeout' | 'security' | 'system' = 'timeout'): Promise<LogoutResponse> {
    const request: LogoutRequest = {
      reason,
      metadata: {
        userAgent: navigator?.userAgent,
        ipAddress: 'auto-logout',
      },
    };

    return await this.execute(request);
  }

  /**
   * 관리자에 의한 강제 로그아웃
   */
  async executeForceLogout(
    targetUserId: string,
    adminUserId: string,
    reason: string = '관리자에 의한 강제 로그아웃'
  ): Promise<LogoutResponse> {
    try {
      const targetUserIdObj = UserId.fromString(targetUserId);
      
      // 모든 기기에서 로그아웃
      await this.authService.logoutFromAllDevices(targetUserIdObj);

      // 보안 이벤트 기록 (관리자 정보 포함)
      const repository = AuthRepositoryFactory.getInstance();
      await repository.recordSecurityEvent({
        userId: targetUserIdObj,
        eventType: 'ADMIN_FORCE_LOGOUT',
        description: `관리자(${adminUserId})에 의한 강제 로그아웃: ${reason}`,
        metadata: {
          adminUserId,
          reason,
          timestamp: new Date().toISOString(),
        },
      });

      return {
        success: true,
        message: '대상 사용자가 모든 기기에서 강제 로그아웃되었습니다.',
        metadata: {
          sessionsCleared: -1,
          securityEventRecorded: true,
          localDataCleared: false, // 관리자 로그아웃이므로 현재 세션은 유지
        },
      };

    } catch (error) {
      console.error('강제 로그아웃 중 오류:', error);
      return {
        success: false,
        error: '강제 로그아웃 처리 중 오류가 발생했습니다.',
        errorCode: 'FORCE_LOGOUT_ERROR',
      };
    }
  }

  /**
   * 로그아웃 이벤트 기록
   */
  private async recordLogoutEvent(
    reason: string,
    metadata?: LogoutRequest['metadata'],
    userId?: UserId
  ): Promise<void> {
    try {
      const repository = AuthRepositoryFactory.getInstance();
      
      // 사용자 ID가 제공되지 않은 경우 현재 사용자 정보에서 가져오기
      let targetUserId = userId;
      if (!targetUserId) {
        const currentUser = AuthLocalStorageAdapter.loadUser();
        if (currentUser?.id) {
          targetUserId = UserId.fromString(currentUser.id);
        }
      }

      if (targetUserId) {
        await repository.recordSecurityEvent({
          userId: targetUserId,
          eventType: 'USER_LOGOUT',
          description: `로그아웃 (사유: ${reason})`,
          ipAddress: metadata?.ipAddress,
          userAgent: metadata?.userAgent,
          metadata: {
            reason,
            deviceName: metadata?.deviceName,
            timestamp: new Date().toISOString(),
          },
        });
      }
    } catch (error) {
      console.error('로그아웃 이벤트 기록 중 오류:', error);
      // 이벤트 기록 실패는 로그아웃 자체를 실패시키지 않음
    }
  }

  /**
   * 현재 로그인 상태 확인
   */
  isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  /**
   * 현재 세션 정보 조회
   */
  getCurrentSessionXCircle(): {
    hasSession: boolean;
    sessionId?: string;
    userId?: string;
    expiresAt?: Date;
    lastActivity?: Date;
  } {
    const session = AuthLocalStorageAdapter.loadSession();
    const user = AuthLocalStorageAdapter.loadUser();
    const lastActivity = AuthLocalStorageAdapter.getLastActivity();

    return {
      hasSession: !!session && !!user,
      sessionId: session?.id,
      userId: user?.id,
      expiresAt: session?.expiresAt ? new Date(session.expiresAt) : undefined,
      lastActivity: lastActivity || undefined,
    };
  }

  /**
   * 세션 만료 시간까지 남은 시간 계산 (밀리초)
   */
  getTimeUntilExpiry(): number {
    const token = AuthLocalStorageAdapter.loadToken();
    if (!token) {
      return 0;
    }

    const expiresAt = new Date(token.expiresAt);
    return Math.max(0, expiresAt.getTime() - Date.now());
  }

  /**
   * 자동 로그아웃 타이머 설정
   */
  setupAutoLogoutTimer(callback?: (reason: string) => void): NodeJS.Timeout | null {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    
    if (timeUntilExpiry <= 0) {
      // 이미 만료된 경우 즉시 로그아웃
      this.executeAutoLogout('timeout').then(() => {
        callback?.('timeout');
      });
      return null;
    }

    // 만료 1분 전에 자동 로그아웃 실행
    const triggerTime = Math.max(timeUntilExpiry - 60000, 0);
    
    return setTimeout(() => {
      this.executeAutoLogout('timeout').then(() => {
        callback?.('timeout');
      });
    }, triggerTime);
  }

  /**
   * 비활성 시간 확인 및 자동 로그아웃
   */
  checkInactivityLogout(): Promise<LogoutResponse | null> {
    if (AuthLocalStorageAdapter.shouldAutoLogout()) {
      return this.executeAutoLogout('timeout');
    }
    return Promise.resolve(null);
  }

  /**
   * 활동 시간 업데이트
   */
  updateActivity(): void {
    this.authService.updateActivity();
  }
}